import { useCallback, useEffect, useMemo, useState } from 'react';
import { Anchor, Archive, ArchiveRestore, ArrowUpRight, Copy, GitBranch, Pencil, Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { fmtCHF2, fmtMoisFr } from '../utils/format';
import type { ProjectionScenario, ScenarioDetaille } from '../types/scenarios';
import { PAGE_PAR_MODULE } from '../types/scenarios';

/**
 * Scénarios : la section où l'on construit les hypothèses, avant d'aller les
 * peindre dans les grilles.
 *
 * Un scénario est **traversant** — il porte des projections de tous les
 * modules (revenus, contrats, mesures, loyers, charges, salaires) et se
 * retrouve comme option dans chaque vue.
 *
 * **Héritage** : un scénario peut avoir un parent. Le lire, c'est lire les
 * décisions prises, plus les projections de toute sa chaîne, ses propres
 * valeurs l'emportant. Modifier « Base » se répercute donc immédiatement dans
 * ses descendants — d'où le geste par défaut : **dériver**, pas dupliquer.
 * Dupliquer reste possible pour figer un instantané (une version présentée au
 * comité, par exemple) : la copie ne suivra plus sa source.
 */
export function ScenariosPage() {
  const [scenarios, setScenarios] = useState<ScenarioDetaille[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [voirArchives, setVoirArchives] = useState(false);
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  // undefined = le parent par défaut (le socle) n'est pas encore connu
  const [parentId, setParentId] = useState<number | null | undefined>(undefined);
  const [enCours, setEnCours] = useState(false);
  const [detail, setDetail] = useState<ScenarioDetaille | null>(null);

  const charger = useCallback(() => {
    setErreur(null);
    api.get<ScenarioDetaille[]>('/scenarios')
      .then(setScenarios)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const modules = useMemo(() => {
    const vus = new Set<string>();
    for (const s of scenarios) for (const m of Object.keys(s.parModule)) vus.add(m);
    // Ordre de lecture : produits d'abord, puis les charges
    const ordre = ['Revenus', 'Contrats', 'Mesures', 'Salaires', 'Loyers', 'Charges'];
    return [...ordre.filter((m) => vus.has(m)), ...[...vus].filter((m) => !ordre.includes(m))];
  }, [scenarios]);

  const visibles = useMemo(
    () => scenarios.filter((s) => voirArchives || !s.archive),
    [scenarios, voirArchives]
  );

  const socle = useMemo(
    () => scenarios.find((s) => s.socle && !s.archive) ?? null,
    [scenarios]
  );

  // Parent par défaut : le socle. Un scénario sans parent n'hérite d'aucune
  // hypothèse — lu dans Budget, il afficherait les décisions seules (donc sans
  // les charges projetées sur le socle) et un résultat faussement flatteur.
  useEffect(() => {
    if (parentId === undefined && !chargement) setParentId(socle?.id ?? null);
  }, [parentId, chargement, socle]);

  async function creer() {
    if (!nom.trim() || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post('/scenarios', {
        nom: nom.trim(), description: description.trim() || null, parentId: parentId ?? null,
      });
      setNom('');
      setDescription('');
      setParentId(socle?.id ?? null);
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  async function renommer(s: ScenarioDetaille) {
    const nouveau = window.prompt(`Nouveau nom pour « ${s.nom} » :`, s.nom);
    if (!nouveau || !nouveau.trim() || nouveau.trim() === s.nom) return;
    setErreur(null);
    try {
      await api.put(`/scenarios/${s.id}`, { nom: nouveau.trim() });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function archiver(s: ScenarioDetaille) {
    setErreur(null);
    try {
      await api.put(`/scenarios/${s.id}`, { archive: !s.archive });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function deriver(s: ScenarioDetaille) {
    const nouveau = window.prompt(
      `Nom du scénario dérivé de « ${s.nom} » ? Il en héritera toutes les hypothèses, `
      + `et suivra ses évolutions ; vous n'y saisirez que les écarts.`,
      `${s.nom} — variante`
    );
    if (!nouveau || !nouveau.trim()) return;
    setErreur(null);
    try {
      await api.post(`/scenarios/${s.id}/deriver`, {
        nom: nouveau.trim(),
        commentaire: `Dérivation du scénario « ${s.nom} »`,
      });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function dupliquer(s: ScenarioDetaille) {
    const nouveau = window.prompt(
      `Nom de la copie figée de « ${s.nom} » ? Ses ${s.nb_evenements} projection`
      + `${s.nb_evenements > 1 ? 's' : ''} propre${s.nb_evenements > 1 ? 's' : ''} seront recopiées `
      + `et la copie **ne suivra plus** les évolutions de la source.`,
      `${s.nom} (copie du jour)`
    );
    if (!nouveau || !nouveau.trim()) return;
    setErreur(null);
    try {
      await api.post(`/scenarios/${s.id}/dupliquer`, {
        nom: nouveau.trim(),
        commentaire: `Duplication (instantané) du scénario « ${s.nom} »`,
      });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  /**
   * Désigner le socle : le budget par défaut de toutes les vues. Un seul à la
   * fois — le serveur démet le précédent.
   */
  async function designerSocle(s: ScenarioDetaille) {
    if (!window.confirm(
      `Désigner « ${s.nom} » comme socle budgétaire ?\n\n`
      + `Toutes les vues (Budget, Revenus, Salaires, Mesures, Loyers, Charges, RH) `
      + `s'ouvriront désormais sur ce scénario`
      + `${socle ? `, et « ${socle.nom} » cessera d'être le socle.` : '.'}`
    )) return;
    setErreur(null);
    try {
      await api.put(`/scenarios/${s.id}`, { socle: true });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  async function supprimer(s: ScenarioDetaille) {
    if (!window.confirm(
      `Supprimer le scénario « ${s.nom} »${s.nb_evenements > 0
        ? ` et ses ${s.nb_evenements} projection${s.nb_evenements > 1 ? 's' : ''}`
        : ''} ? Cette action est irréversible.`
    )) return;
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/scenarios/${s.id}`, { commentaire: c.trim() });
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Scénarios</h1>
          <span className="stat-pill">{scenarios.filter((s) => !s.archive).length} actifs</span>
        </div>
        <label className="toggle-label">
          <span className="toggle">
            <input type="checkbox" className="toggle-input" checked={voirArchives}
              onChange={(e) => setVoirArchives(e.target.checked)} />
            <span className="toggle-slider" />
          </span>
          <span className="text-sm">Voir les archivés</span>
        </label>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="formulaire-changement mb-16">
        <div className="formulaire-changement__titre">Nouveau scénario</div>
        <div className="flex gap-12 flex-wrap items-end">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Nom</label>
            <input className="form-input" style={{ width: 200 }} placeholder="Ex. CTT supprimé dès 2027"
              value={nom} onChange={(e) => setNom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') creer(); }} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Dérivé de <span className="text-ink-3">(hérite de ses hypothèses)</span></label>
            <select className="form-input" style={{ width: 190 }} value={parentId ?? ''}
              title="Un scénario hérite des hypothèses de son parent. Sans parent, il ne porte
                ni les charges ni les revenus projetés du budget."
              onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— aucun parent (déconseillé : n'hérite pas du budget) —</option>
              {scenarios.filter((s) => !s.archive).map((s) => (
                <option key={s.id} value={s.id}>{s.nom}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
            <label className="form-label">Hypothèses du scénario</label>
            <input className="form-input w-full" placeholder="Ce que ce scénario suppose, et pourquoi…"
              value={description} onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') creer(); }} />
          </div>
          <button className="btn btn-accent" disabled={!nom.trim() || enCours} onClick={creer}>
            {enCours ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>

      <table className="table table-compact">
        <thead>
          <tr>
            <th>Scénario</th>
            <th>Dérivé de</th>
            {modules.map((m) => <th key={m} className="text-right">{m}</th>)}
            <th className="text-right">Total</th>
            <th>Statut</th>
            <th style={{ width: 186 }} />
          </tr>
        </thead>
        <tbody>
          {visibles.map((s) => {
            // Socle et parents ne se retirent pas : leurs descendants lisent
            // leurs projections par héritage. Le serveur refuse (409) ; on
            // désactive ici pour que la raison se lise avant le clic.
            const enfants = scenarios.filter((x) => x.parent_id === s.id);
            const verrou = s.socle
              ? `Socle des scénarios : tous les autres en héritent. Désignez un autre socle pour pouvoir l'archiver ou le supprimer.`
              : enfants.length
                ? `Parent de ${enfants.map((e) => `« ${e.nom} »`).join(', ')} : réaffectez `
                  + `${enfants.length > 1 ? 'ces scénarios' : 'ce scénario'} avant de l'archiver ou de le supprimer.`
                : null;
            return (
            <tr key={s.id} style={s.archive ? { opacity: 0.55 } : undefined}>
              <td style={{ cursor: 'pointer' }} onClick={() => setDetail(s)}>
                <span className="font-semi">{s.nom}</span>
                {s.description && <span className="text-xs text-ink-3"> — {s.description}</span>}
              </td>
              <td className="text-sm">
                {s.parent_nom
                  ? <span className="badge badge-sm" title="Hérite des hypothèses de ce scénario et suit ses évolutions">
                      {s.parent_nom}
                    </span>
                  : s.socle
                    ? <span className="text-ink-3" title="Socle budgétaire : la racine de l'héritage">racine</span>
                    : <span className="badge badge-err badge-sm"
                        title="Aucun parent : ce scénario n'hérite d'aucune hypothèse — lu dans Budget, il n'affiche que les décisions arrêtées (ni charges ni revenus projetés). Le dériver du socle.">
                        aucun parent
                      </span>}
              </td>
              {modules.map((m) => (
                <td key={m} className="tabular-nums text-right">
                  {s.parModule[m] ? s.parModule[m] : <span className="text-ink-3">—</span>}
                </td>
              ))}
              <td className="tabular-nums text-right font-semi">{s.nb_evenements}</td>
              <td>
                <div className="flex gap-4 items-center">
                  {s.archive
                    ? <span className="badge badge-sm">archivé</span>
                    : <span className="badge badge-ok badge-sm">actif</span>}
                  {s.socle && (
                    <span className="badge badge-accent badge-sm"
                      title="Socle budgétaire : le budget en l'état, lecture par défaut de toutes les vues">
                      socle
                    </span>
                  )}
                </div>
              </td>
              <td>
                <div className="flex gap-4 justify-end">
                  <button className="btn btn-icon" title="Renommer" onClick={() => renommer(s)}>
                    <Pencil className="icon-sm" />
                  </button>
                  <button className="btn btn-icon"
                    disabled={s.socle || s.archive}
                    title={s.socle
                      ? 'Socle actuel : toutes les vues s’ouvrent déjà dessus'
                      : s.archive
                        ? 'Restaurer le scénario avant d’en faire le socle'
                        : 'Désigner comme socle : ce scénario devient le budget par défaut de toutes les vues'}
                    onClick={() => designerSocle(s)}>
                    <Anchor className="icon-sm" />
                  </button>
                  <button className="btn btn-icon" title="Dériver : créer une variante qui hérite de ce scénario et suit ses évolutions"
                    onClick={() => deriver(s)}>
                    <GitBranch className="icon-sm" />
                  </button>
                  <button className="btn btn-icon" title="Dupliquer : copie figée, qui ne suivra plus les évolutions de la source"
                    onClick={() => dupliquer(s)}>
                    <Copy className="icon-sm" />
                  </button>
                  <button className="btn btn-icon"
                    disabled={!s.archive && verrou != null}
                    title={s.archive
                      ? 'Restaurer'
                      : verrou ?? 'Archiver (masque le scénario sans le supprimer)'}
                    onClick={() => archiver(s)}>
                    {s.archive ? <ArchiveRestore className="icon-sm" /> : <Archive className="icon-sm" />}
                  </button>
                  <button className="btn btn-icon"
                    disabled={verrou != null}
                    title={verrou ?? 'Supprimer le scénario et ses projections'}
                    onClick={() => supprimer(s)}>
                    <Trash2 className="icon-sm" />
                  </button>
                </div>
              </td>
            </tr>
            );
          })}
          {visibles.length === 0 && (
            <tr>
              <td colSpan={modules.length + 4} className="text-ink-3">
                Aucun scénario — en créer un pour commencer à poser des hypothèses.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <p className="text-xs text-ink-3 mt-8">
        Un scénario est traversant : il se retrouve comme option dans Budget, Revenus, Salaires, Mesures,
        Loyers et Charges. Un scénario <strong>dérivé</strong> hérite des hypothèses de son parent et suit
        ses évolutions — les décomptes ci-dessus ne montrent donc que ses valeurs <strong>propres</strong>,
        c'est-à-dire ses écarts. Modifier le socle « Base » se répercute d'office dans tous ses descendants.
        Les projections se saisissent dans les grilles (statut « projeté ») ; cette page sert à créer,
        dériver et relire les scénarios comme un tout. Le <strong>socle</strong> (⚓) est le budget en
        l'état : toutes les vues s'ouvrent dessus, et c'est le parent proposé par défaut à la création.
        Un scénario <strong>sans parent</strong> n'hérite de rien — lu dans Budget, il n'affiche que les
        décisions arrêtées.
      </p>

      {detail && (
        <ScenarioModal
          scenario={detail}
          onFermer={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function ScenarioModal({ scenario, onFermer }: { scenario: ScenarioDetaille; onFermer: () => void }) {
  const [projections, setProjections] = useState<ProjectionScenario[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ projections: ProjectionScenario[] }>(`/scenarios/${scenario.id}`)
      .then((r) => setProjections(r.projections))
      .catch((e) => setErreur(e.message));
  }, [scenario.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const parModule = useMemo(() => {
    const groupes = new Map<string, ProjectionScenario[]>();
    for (const p of projections ?? []) {
      if (!groupes.has(p.module)) groupes.set(p.module, []);
      groupes.get(p.module)!.push(p);
    }
    return [...groupes.entries()];
  }, [projections]);

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{scenario.nom}</div>
            <div className="text-xs text-ink-3">
              {scenario.description ? `${scenario.description} · ` : ''}
              {scenario.nb_evenements} projection{scenario.nb_evenements > 1 ? 's' : ''}
              {scenario.auteur ? ` · créé par ${scenario.auteur}` : ''}
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {erreur && <div className="text-err mb-16">{erreur}</div>}
          {projections == null && <div className="spinner" />}
          {projections != null && projections.length === 0 && (
            <p className="text-ink-3">
              Ce scénario ne porte encore aucune hypothèse. Ouvrir une grille (Revenus, Salaires, Loyers,
              Charges…), double-cliquer une cellule et enregistrer la valeur en « projeté » sur ce scénario.
            </p>
          )}
          {parModule.map(([module, lignes]) => (
            <div key={module} className="mb-16">
              <div className="flex items-center gap-8 mb-8">
                <span className="font-semi">{module}</span>
                <span className="stat-pill">{lignes.length}</span>
                {PAGE_PAR_MODULE[module] && (
                  <a className="text-xs" href={`#${PAGE_PAR_MODULE[module]}`}
                    title={`Ouvrir le module ${module}`}>
                    voir la grille <ArrowUpRight className="icon-sm" />
                  </a>
                )}
              </div>
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Ligne</th>
                    <th>Dès</th>
                    <th className="text-right">Valeur</th>
                    <th>Commentaire</th>
                    <th>Auteur</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((p) => (
                    <tr key={`${p.table}-${p.id}`}>
                      <td>{p.ligne}</td>
                      <td className="tabular-nums">{fmtMoisFr(p.datation)}</td>
                      <td className="tabular-nums text-right">
                        {p.valeur != null ? `${fmtCHF2(p.valeur)}${p.unite ? ` ${p.unite}` : ''}` : '—'}
                      </td>
                      <td className="text-sm text-ink-2">{p.commentaire}</td>
                      <td className="text-xs text-ink-3">{p.auteur ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
